import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import type { IAppsAdSellersFilterItem } from "app/pages/sales-intelligence/domain/apps/types/filter-values";
import type { AppOperatingSystem } from "app/pages/sales-intelligence/domain/apps/types/platform";
import type { IAppsSdkDescriptionItemDto } from "app/pages/sales-intelligence/domain/sdk/types/dto";
import type { IAppsSDKsFilterItem } from "app/pages/sales-intelligence/domain/sdk/types/filter-values";
import type { IRange } from "app/pages/sales-intelligence/types/range";
import type { TabbedDropdownItemType } from "app/pages/sales-intelligence/types/types";
import { haveSameElements } from "app/pages/sales-intelligence/utils/arrays";

import type { IAppsSDKsFilterDescriptionDto } from "../types/descriptionDto";
import type { DisabledDropdownSdkValues } from "../types/dropdown";

export const getSdkFilterByPlatform = (
    sdkFilter: IAppsSDKsFilterDescriptionDto | undefined,
    market: AppOperatingSystem,
): IAppsSDKsFilterDescriptionDto | undefined => {
    if (sdkFilter) {
        return {
            ...sdkFilter,
            sdksDescriptions: sdkFilter.sdksDescriptions.filter((sdkDescription: IAppsSdkDescriptionItemDto) =>
                sdkDescription.markets.includes(market),
            ),
        };
    }

    return;
};

export const getDisabledSdkValues = (
    items: readonly IAppsSDKsFilterItem[],
    selected: IAppsSDKsFilterItem | null,
): DisabledDropdownSdkValues => {
    return items.reduce(
        (acc: { categories: string[]; sdks: string[] }, item: IAppsSDKsFilterItem) => {
            const filteredCategories = selected
                ? item.categories.filter((category) => !selected.categories.includes(category))
                : item.categories;

            const filteredSdk = selected ? item.sdks.filter((sdk: string) => selected.sdks.includes(sdk)) : item.sdks;
            acc.categories.push(...filteredCategories);
            acc.sdks.push(...filteredSdk);
            return acc;
        },
        { categories: [], sdks: [] },
    );
};

export const getSdkCategoryDDItems = (
    item: IAppsSdkDescriptionItemDto,
    disabledValues: DisabledDropdownSdkValues,
    sdkItem: IAppsSDKsFilterItem,
): Pick<TabbedDropdownItemType, "name" | "isSelected" | "isDisabled" | "secondaryName"> => {
    const isDisabled = disabledValues.sdks.includes(item.name);
    const isDisabledByCategory = item.categories.some((category) => disabledValues.categories.includes(category));

    const isSelected = sdkItem.sdks.includes(item.name);
    return {
        name: item.name,
        secondaryName: item.categories.join(" | "),
        isSelected: isDisabled || isDisabledByCategory || isSelected,
        isDisabled: (isDisabled || isDisabledByCategory) && !isSelected,
    };
};

export const getSdksDDItem = (
    item: string,
    disabledValues: DisabledDropdownSdkValues,
    sdkItem: IAppsSDKsFilterItem,
): Pick<TabbedDropdownItemType, "name" | "isSelected" | "isDisabled"> => {
    const isDisabled = disabledValues.categories.includes(item);
    const isSelected = sdkItem.categories.includes(item);
    return {
        name: item,
        isSelected: isDisabled || isSelected,
        isDisabled: isDisabled && !isSelected,
    };
};

export const getAllCategoriesFromSdkDto = (
    sdkDescriptions: readonly IAppsSdkDescriptionItemDto[],
): readonly string[] => {
    return sdkDescriptions.reduce((acc: string[], sdk) => {
        acc.push(...sdk.categories);
        return acc;
    }, []);
};

export const hasSameSdkObject = (item: IAppsSDKsFilterItem, comparedItem: IAppsSDKsFilterItem | null): boolean => {
    if (comparedItem === null) {
        return false;
    }

    return (
        item.inclusion === comparedItem.inclusion &&
        item.operator === comparedItem.operator &&
        haveSameElements(item.sdks, comparedItem.sdks) &&
        haveSameElements(item.categories, comparedItem.categories)
    );
};

export const hasSameAdsSellersObject = (
    item: IAppsAdSellersFilterItem,
    comparedItem: IAppsAdSellersFilterItem | null,
): boolean => {
    if (comparedItem === null) {
        return false;
    }

    return (
        item.inclusion === comparedItem.inclusion &&
        item.operator === comparedItem.operator &&
        haveSameElements(item.adsSellerDomainNames, comparedItem.adsSellerDomainNames)
    );
};

export const getDisabledAdsSellersValues = (
    items: readonly IAppsAdSellersFilterItem[] | null | undefined,
    selected: IAppsAdSellersFilterItem | null,
    currentTarget: IAppsAdSellersFilterItem | null,
): readonly string[] => {
    const allDomainNames = new Set<string>();
    const selectedDomains = new Set([
        ...(selected?.adsSellerDomainNames ?? []),
        ...(currentTarget?.adsSellerDomainNames ?? []),
    ]);

    items?.forEach((item) => {
        item.adsSellerDomainNames.forEach((domain) => {
            if (!selectedDomains.has(domain)) {
                allDomainNames.add(domain);
            }
        });
    });

    return Array.from(allDomainNames);
};

export const formatDisplayedValue = (value: number | string): string => abbrNumberFilter()(value);

export const getRangeChipValue = (
    range?: Partial<IRange<number | string>>,
    withFormatting = true,
): string | undefined => {
    const formatValue = withFormatting ? formatDisplayedValue : (value: number | string) => value;

    if (range) {
        if (range.from && range.to) {
            return `${formatValue(range.from)} - ${formatValue(range.to)}`;
        }

        if (range.to && !range.from) {
            return translate("si.common.range.to", {
                to: formatValue(range.to),
            });
        }

        if (!range.to && range.from) {
            return translate("si.common.range.from", {
                from: formatValue(range.from),
            });
        }
    }

    return;
};
